import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';
import { FeatureFlagKey } from '@dwo/enums';
import { WorkOrderDetailsDto } from '@app/generated/model';
import { WorkOrderDetailsService } from '../work-order-details.service';
import { WorkOrderSiteQueryService } from './site-query.service';
import { WorkOrderSiteQuery } from './site-query.model';

@Component({
  selector: 'app-work-order-site-queries',
  templateUrl: './work-order-site-queries.component.html',
  styleUrls: ['./site-query.component.scss'],
  standalone: false,
})
export class WorkOrderSiteQueriesComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private workOrderId = '';

  workOrder: WorkOrderDetailsDto | null = null;
  siteQueries: WorkOrderSiteQuery[] = [];
  loading = true;
  loadFailed = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly workOrderDetailsService: WorkOrderDetailsService,
    private readonly siteQueryService: WorkOrderSiteQueryService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.workOrderId = this.getWorkOrderId();
    this.workOrderDetailsService
      .getData()
      .pipe(
        filter((workOrder): workOrder is WorkOrderDetailsDto => !!workOrder && workOrder.id === this.workOrderId),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((workOrder) => {
        this.workOrder = workOrder;
        this.changeDetectorRef.markForCheck();
        if (!this.featureFlagService.isEnabled(FeatureFlagKey.EnableSiteQuery, workOrder.projectId)) {
          void this.router.navigate(['/work-order', this.workOrderId], { replaceUrl: true });
          return;
        }
        this.loadSiteQueries();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createSiteQuery(): void {
    void this.router.navigate(['/work-order', this.workOrderId, 'site-queries', 'new']);
  }

  async downloadAttachment(siteQuery: WorkOrderSiteQuery, attachmentId: string, fileName: string): Promise<void> {
    try {
      const blob = await firstValueFrom(
        this.siteQueryService.downloadAttachment(this.workOrderId, siteQuery.id, attachmentId),
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      anchor.rel = 'noopener noreferrer';
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      this.snackBar.open(this.translate.instant('siteQuery.attachmentDownloadError'), 'OK', { duration: 5000 });
    }
  }

  private loadSiteQueries(): void {
    this.loading = true;
    this.loadFailed = false;
    this.siteQueryService
      .list(this.workOrderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (siteQueries) => {
          this.siteQueries = siteQueries ?? [];
          this.loading = false;
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.loadFailed = true;
          this.loading = false;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  private getWorkOrderId(): string {
    return this.route.parent?.parent?.snapshot.paramMap.get('id') ?? this.route.snapshot.paramMap.get('id') ?? '';
  }
}
